/*
 * <card-media-carousel>
 *
 * Controalele (săgeți + liniuțe) pentru caruselul de imagini din cardul de produs.
 * Elementul stă ca frate al <a class="card"> în .card__picture-container, deci
 * click-ul pe săgeți nu poate naviga la produs (buton în interiorul unui link = HTML invalid).
 *
 * Imaginile trăiesc mai departe în <picture class="card__picture">:
 *   - slide 0  = .card__img            (src/srcset reale, contează pentru LCP)
 *   - slide 1+ = .card__img--slide     (data-src/data-srcset, hidratate la prima interacțiune)
 *
 * Custom element, nu init global: grid-ul de produse e re-randat de facets.js,
 * color-swatch.js, slider.js, collection-section.js, recently-viewed-products.js
 * și quick-add.js. connectedCallback() rulează și pe cardurile injectate ulterior.
 */
if (!customElements.get("card-media-carousel")) {
  customElements.define(
    "card-media-carousel",
    class CardMediaCarousel extends HTMLElement {
      static SWIPE_THRESHOLD = 30;

      connectedCallback() {
        if (this._ready) return;

        this.container = this.closest(".card__picture-container");
        if (!this.container) return;

        this.link = this.container.querySelector("a.card");
        this.slides = Array.from(
          this.container.querySelectorAll(".card__img"),
        ).filter((node) => node.tagName === "IMG");

        if (this.slides.length < 2) {
          this.hidden = true;
          return;
        }

        this.dots = Array.from(this.querySelectorAll(".card__carousel-dot"));
        this.prevBtn = this.querySelector("[data-carousel-prev]");
        this.nextBtn = this.querySelector("[data-carousel-next]");

        this.index = 0;
        this.tracking = false;
        this.locked = false;
        this.swipeHandled = false;

        this._onPrev = (event) => this.step(event, -1);
        this._onNext = (event) => this.step(event, 1);
        this._onEnter = () => this.ensure(1);
        this._onLeave = () => this.goTo(0);
        this._onTouchStart = (event) => this.touchStart(event);
        this._onTouchMove = (event) => this.touchMove(event);
        this._onTouchEnd = (event) => this.touchEnd(event);
        this._onLinkClick = (event) => {
          if (!this.swipeHandled) return;
          this.swipeHandled = false;
          event.preventDefault();
          event.stopPropagation();
        };

        this.prevBtn?.addEventListener("click", this._onPrev);
        this.nextBtn?.addEventListener("click", this._onNext);
        this.container.addEventListener("mouseenter", this._onEnter);
        this.container.addEventListener("mouseleave", this._onLeave);
        this.link?.addEventListener("click", this._onLinkClick, true);

        // Listenerii stau pe container, nu pe <picture>: pe mobil săgețile ocupă
        // 44px din fiecare margine, iar swipe-ul trebuie să funcționeze și peste ele.
        this.container.addEventListener("touchstart", this._onTouchStart, {
          passive: true,
        });
        this.container.addEventListener("touchmove", this._onTouchMove, {
          passive: true,
        });
        this.container.addEventListener("touchend", this._onTouchEnd, {
          passive: true,
        });
        this.container.addEventListener("touchcancel", this._onTouchEnd, {
          passive: true,
        });

        this._ready = true;
      }

      disconnectedCallback() {
        if (!this._ready) return;

        this.prevBtn?.removeEventListener("click", this._onPrev);
        this.nextBtn?.removeEventListener("click", this._onNext);
        this.container?.removeEventListener("mouseenter", this._onEnter);
        this.container?.removeEventListener("mouseleave", this._onLeave);
        this.link?.removeEventListener("click", this._onLinkClick, true);

        this.container?.removeEventListener("touchstart", this._onTouchStart);
        this.container?.removeEventListener("touchmove", this._onTouchMove);
        this.container?.removeEventListener("touchend", this._onTouchEnd);
        this.container?.removeEventListener("touchcancel", this._onTouchEnd);

        this._ready = false;
      }

      /* Populează src/srcset pentru un singur slide, la cerere. */
      ensure(position) {
        const total = this.slides.length;
        const slide = this.slides[((position % total) + total) % total];
        if (!slide || !slide.dataset.src) return;

        if (slide.dataset.srcset) {
          slide.setAttribute("srcset", slide.dataset.srcset);
          delete slide.dataset.srcset;
        }
        slide.setAttribute("src", slide.dataset.src);
        delete slide.dataset.src;
      }

      step(event, delta) {
        // Plasă de siguranță: săgețile sunt în afara <a>, dar nu vrem niciun click scurs mai departe.
        event.preventDefault();
        event.stopPropagation();
        this.goTo(this.index + delta);
      }

      goTo(position) {
        const total = this.slides.length;
        const next = ((position % total) + total) % total;

        this.ensure(next);
        this.index = next;
        this.sync();

        // Pre-încarcă vecinii ca următorul click să fie instant.
        this.ensure(next + 1);
        this.ensure(next - 1);
      }

      sync() {
        this.slides.forEach((slide, position) => {
          slide.classList.toggle("card__img--active", position === this.index);
        });
        this.dots.forEach((dot, position) => {
          dot.classList.toggle("is-active", position === this.index);
        });
      }

      /*
       * Dacă cardul stă într-un slider Swiper (homepage, produse similare,
       * recently viewed, collection navigator), swipe-ul orizontal aparține
       * sliderului. Săgețile rămân funcționale acolo.
       */
      sliderAncestor() {
        let node = this.parentElement;
        while (node && node !== document.body) {
          if (node.swiper) return node;
          node = node.parentElement;
        }
        return null;
      }

      touchStart(event) {
        this.tracking = false;
        this.locked = false;
        if (event.touches.length !== 1) return;
        if (this.sliderAncestor()?.swiper?.allowTouchMove) return;

        this.startX = event.touches[0].clientX;
        this.startY = event.touches[0].clientY;
        this.tracking = true;
        this.ensure(1);
      }

      touchMove(event) {
        if (!this.tracking || event.touches.length !== 1) return;

        const deltaX = event.touches[0].clientX - this.startX;
        const deltaY = event.touches[0].clientY - this.startY;

        if (!this.locked) {
          if (Math.abs(deltaY) > Math.abs(deltaX)) {
            // Scroll vertical — lasă pagina în pace.
            this.tracking = false;
            return;
          }
          if (Math.abs(deltaX) < CardMediaCarousel.SWIPE_THRESHOLD) return;
          this.locked = true;
        }

        event.stopPropagation();
      }

      touchEnd(event) {
        if (this.tracking && this.locked) {
          const touch = event.changedTouches && event.changedTouches[0];
          const deltaX = touch ? touch.clientX - this.startX : 0;

          this.goTo(this.index + (deltaX < 0 ? 1 : -1));
          this.swipeHandled = true;
          event.stopPropagation();
          setTimeout(() => {
            this.swipeHandled = false;
          }, 400);
        }

        this.tracking = false;
        this.locked = false;
      }
    },
  );
}
